import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, map, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SKIP_LOADING } from '../interceptors/loading.interceptor';

/** Roles permitted to sign in to Invento Studio. */
export const STUDIO_ALLOWED_ROLES = ['Invento-Studio Admin'] as const;

/** Raw response from POST /api/Identity/authenticate. */
export interface LoginResponse {
  flag: boolean;
  message: string;
  token: string;
  email: string;
  role: string;
  refreshToken: string;
  userId: string;
  tenantId: string;
}

/** Raw response from POST /api/Identity/RefreshToken. */
export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
  message: string;
}

/** Subset of POST /api/Identity/UserLoginDetails needed for password reset. */
export interface UserLoginDetails {
  userId: string;
  tenantId: string;
  firstName: string | null;
  userEmailAddress: string | null;
  /** Comma-separated role names, matching the API's UserRoleNames field. */
  userRoleNames: string | null;
  resetToken: string | null;
  resetTokenExpirationdate: string | null;
}

/** Response from POST /api/Identity/ReSetUserPassword. */
export interface ResetPasswordResponse {
  flag: boolean;
  message: string;
}

/** The authenticated session we persist locally. */
export interface AuthSession {
  token: string;
  refreshToken: string;
  email: string;
  role: string;
  name: string;
  userId: string;
  tenantId: string;
  /** Whether this session was persisted to localStorage ("remember me"). */
  persist: boolean;
}

const SESSION_KEY = 'invento_studio_session';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);

  private readonly _session = signal<AuthSession | null>(this.readStoredSession());

  /** Current session (null when signed out). */
  readonly session = this._session.asReadonly();
  readonly isAuthenticated = computed(() => this._session() !== null);
  readonly role = computed(() => this._session()?.role ?? null);
  readonly email = computed(() => this._session()?.email ?? null);
  readonly tenantId = computed(() => this._session()?.tenantId ?? null);
  readonly userId = computed(() => this._session()?.userId ?? null);

  /**
   * Authenticates against the shared Identity API, flagging this as a Studio
   * login so the backend enforces the Studio role gate. On success the session
   * (token, refresh token, tenant, role) is persisted and exposed via signals.
   */
  login(emailOrPhone: string, password: string, rememberMe: boolean): Observable<LoginResponse> {
    const body = {
      EmailOrPhoneNumber: emailOrPhone,
      Password: password,
      Type: 'Email',
      AppSource: 'Studio',
    };

    return this.http
      .post<LoginResponse>(`${environment.apiBase}/Identity/authenticate`, body, {
        // The Sign In button shows its own inline spinner, so skip the global
        // loading overlay for this request.
        context: new HttpContext().set(SKIP_LOADING, true),
      })
      .pipe(
        tap((res) => {
          if (res?.flag && res.token) {
            this.setSession(
              {
                token: res.token,
                refreshToken: res.refreshToken,
                email: res.email,
                role: res.role,
                name: (res.email ?? '').split('@')[0],
                userId: res.userId,
                tenantId: res.tenantId,
                persist: rememberMe,
              },
              rememberMe,
            );
          }
        }),
      );
  }

  /**
   * Exchanges the current access + refresh token for a fresh pair. Emits the new
   * access token on success, or null on failure (caller should then log out).
   * Updates the stored session in place when successful.
   */
  refreshToken(): Observable<string | null> {
    const session = this._session();
    if (!session?.refreshToken || !session.token) {
      return of(null);
    }

    const body = {
      AccessToken: session.token,
      RefreshToken: session.refreshToken,
      UserProfileId: session.userId,
      TenantId: session.tenantId,
      Email: session.email,
      Phonenumber: null,
    };

    return this.http
      .post<RefreshTokenResponse>(`${environment.apiBase}/Identity/RefreshToken`, body, {
        // Token refresh is a background operation (proactive timer, idle "stay
        // signed in", or an interceptor 401 retry) — never show the global overlay.
        context: new HttpContext().set(SKIP_LOADING, true),
      })
      .pipe(
        map((res) => {
          if (res?.accessToken && res.message === 'Token Valid') {
            this.setSession(
              { ...session, token: res.accessToken, refreshToken: res.refreshToken },
              session.persist,
            );
            return res.accessToken;
          }
          return null;
        }),
      );
  }

  /**
   * Looks up a user's login details (id, tenant, reset token, role) by email.
   * Used by the forgot/reset-password flow. This is an anonymous endpoint.
   */
  getUserDetails(email: string): Observable<UserLoginDetails | null> {
    return this.http.post<UserLoginDetails | null>(
      `${environment.apiBase}/Identity/UserLoginDetails`,
      { PhoneNumber: null, Email: email },
      { context: new HttpContext().set(SKIP_LOADING, true) },
    );
  }

  /**
   * Starts the password-reset flow. The server validates the account and role,
   * generates the token, and sends the reset email — the client only supplies
   * the address and always receives a neutral response.
   */
  forgotPassword(email: string): Observable<{ flag: boolean; message: string }> {
    return this.http.post<{ flag: boolean; message: string }>(
      `${environment.apiBase}/Identity/ForgotPassword`,
      { Email: email },
      { context: new HttpContext().set(SKIP_LOADING, true) },
    );
  }

  /** Sets a new password given a valid reset token. */
  resetPassword(
    userId: string,
    password: string,
    email: string,
    token: string,
  ): Observable<ResetPasswordResponse> {
    return this.http.post<ResetPasswordResponse>(
      `${environment.apiBase}/Identity/ReSetUserPassword`,
      { userId, password, email, token },
      { context: new HttpContext().set(SKIP_LOADING, true) },
    );
  }

  /** Returns the current bearer token, or null. */
  getToken(): string | null {
    return this._session()?.token ?? null;
  }

  /**
   * True when the given role value grants Studio access. The value may be a
   * single role or a comma-separated list (e.g. "Underwriter-Admin, Invento-Studio Admin"),
   * so we check whether any allowed role is present.
   */
  isStudioRole(role: string | null | undefined): boolean {
    if (!role) return false;
    const roles = role.split(',').map((r) => r.trim());
    return roles.some((r) => (STUDIO_ALLOWED_ROLES as readonly string[]).includes(r));
  }

  /** True when the signed-in role is permitted in Studio. */
  hasStudioAccess(): boolean {
    return this.isStudioRole(this._session()?.role);
  }

  /** Clears the session from memory and both storages. */
  logout(): void {
    this._session.set(null);
    localStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_KEY);
  }

  private setSession(session: AuthSession, persist: boolean): void {
    this._session.set(session);
    // "Remember me" -> localStorage (survives browser restart); otherwise sessionStorage.
    const store = persist ? localStorage : sessionStorage;
    const other = persist ? sessionStorage : localStorage;
    other.removeItem(SESSION_KEY);
    store.setItem(SESSION_KEY, JSON.stringify(session));
  }

  private readStoredSession(): AuthSession | null {
    const raw = localStorage.getItem(SESSION_KEY) ?? sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as AuthSession;
      return parsed?.token ? parsed : null;
    } catch {
      return null;
    }
  }
}
